import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calendar, MapPin, CarFront, Receipt, FileText, Loader2 } from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-slate-100 text-slate-600",
  confirmed: "bg-emerald-100 text-emerald-700",
  active: "bg-blue-100 text-blue-700",
  completed: "bg-slate-100 text-slate-500",
  cancelled: "bg-red-100 text-red-600",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    api.get("/bookings/me").then((res) => setBookings(res.data)).finally(() => setLoading(false));
  }, []);

  const downloadContract = async (b) => {
    setDownloadingId(b.id);
    try {
      const res = await api.get(`/bookings/${b.id}/contract`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `AAR-contract-${b.id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate contract. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const active = bookings.filter((b) => ["confirmed", "active"].includes(b.status) && b.payment_status === "paid");

  return (
    <div className="App bg-slate-50 min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950" data-testid="dashboard-title">
              Hi {user?.name?.split(" ")[0]} 👋
            </h1>
            <p className="mt-2 text-slate-500">Manage your reservations and trip history.</p>
          </div>
          <Button onClick={() => navigate("/fleet")} className="bg-emerald-900 hover:bg-emerald-800 rounded-full px-6" data-testid="browse-fleet-cta">
            Book a new ride
          </Button>
        </div>

        {active.length > 0 && (
          <div className="mt-8 bg-emerald-950 emerald-grain rounded-3xl p-6 sm:p-8 text-white" data-testid="active-rental-banner">
            <span className="text-emerald-300 text-sm font-medium">Active reservation</span>
            <div className="mt-3 flex items-center gap-4 flex-wrap">
              <img src={active[0].car_image} alt="" className="h-20 w-28 object-cover rounded-xl" />
              <div>
                <h3 className="font-display font-bold text-xl">{active[0].car_name}</h3>
                <p className="text-emerald-100/70 text-sm mt-1">
                  {active[0].pickup_date} → {active[0].return_date} · {active[0].pickup_location}
                </p>
              </div>
            </div>
          </div>
        )}

        <h2 className="mt-10 font-display text-xl font-bold text-emerald-950">Your bookings</h2>
        {loading ? (
          <p className="text-slate-400 py-16 text-center" data-testid="dashboard-loading">Loading…</p>
        ) : bookings.length === 0 ? (
          <div className="mt-6 bg-white rounded-3xl border border-slate-200 p-12 text-center" data-testid="no-bookings">
            <CarFront className="mx-auto text-emerald-300" size={40} />
            <p className="mt-4 text-slate-500">You haven't booked any rides yet.</p>
            <Button onClick={() => navigate("/fleet")} className="mt-4 bg-emerald-900 hover:bg-emerald-800 rounded-full">Browse the fleet</Button>
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="bookings-list">
            {bookings.map((b, i) => (
              <div key={b.id} className="bg-white rounded-2xl border border-slate-200/70 p-5 flex items-center gap-5 flex-wrap" data-testid={`booking-row-${i}`}>
                <img src={b.car_image} alt="" className="h-16 w-24 object-cover rounded-xl bg-slate-100" />
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-bold text-emerald-950">{b.car_name}</h3>
                    <Badge className={`${STATUS_STYLE[b.status]} border-0 capitalize`}>{b.status}</Badge>
                    {b.payment_status === "paid" && <Badge className="bg-emerald-100 text-emerald-700 border-0">Paid</Badge>}
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                    <span className="flex items-center gap-1.5"><Calendar size={14} /> {b.pickup_date} → {b.return_date}</span>
                    <span className="flex items-center gap-1.5"><MapPin size={14} /> {b.pickup_location}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-display font-extrabold text-xl text-emerald-900">${b.total.toFixed(2)}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1 justify-end"><Receipt size={12} /> {b.days} day(s)</p>
                </div>
                {b.payment_status !== "paid" && b.status !== "cancelled" && (
                  <Button size="sm" onClick={() => navigate(`/cars/${b.car_id}`)} className="bg-amber-500 hover:bg-amber-600 rounded-full" data-testid={`complete-payment-${i}`}>
                    Complete payment
                  </Button>
                )}
                {b.status !== "cancelled" && (
                  <Button size="sm" variant="outline" onClick={() => downloadContract(b)} disabled={downloadingId === b.id} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-full" data-testid={`download-contract-${i}`}>
                    {downloadingId === b.id ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <FileText size={15} className="mr-1.5" />}
                    Contract
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
